import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { formatDate } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';

import { InvestmentInCompaniesDepositsModel } from '../../../../../modules/investment/incompanies/investment-incompanies.model';
import { InvestmentInCompaniesService } from '../../../../../modules/investment/incompanies/investment-incompanies.service';
import { InvestmentInCompaniesDialogPayComponent } from '../../../../../modules/investment/incompanies/investment-incompanies-dialog-pay.component';
import { ActualSelected, Utils } from '../../../../../shared/utils';
import { ConfirmService } from '../../../../shared/services/confirm.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { TierService } from '../../../../shared/services/tier.service';
import { ConfirmDialogDetail } from '../../../../shared/components/confirm-dialog.component';
import {
  InvestmentCompanyRow,
  investmentPaymentLabel,
  investmentPaymentVariant,
  roundMoney
} from '../../models/investment-company.model';
import { InvestmentCompanyEntityService } from '../../services/investment-company-entity.service';

interface InvestmentDepositLine {
  uid: string;
  date: Date | string;
  concept: string;
  meta: string;
  amount: number;
  source: InvestmentInCompaniesDepositsModel;
}

const MAX_LINES = 5;

@Component({
  selector: 'app-investment-company-detail',
  templateUrl: './investment-company-detail.component.html',
  styleUrls: ['./investment-company-detail.component.scss'],
  standalone: false,
})
export class InvestmentCompanyDetailComponent implements OnChanges {
  @Input() row!: InvestmentCompanyRow;
  @Output() changed = new EventEmitter<void>();
  @Output() edit = new EventEmitter<void>();
  @Output() download = new EventEmitter<void>();

  readonly maxLines = MAX_LINES;
  readonly year = Number(ActualSelected.yearSelected);

  loading = true;
  error = false;

  lines: InvestmentDepositLine[] = [];
  yearCount = 0;
  yearTotal = 0;
  depositsCount = 0;
  commissionsTotal = 0;

  private loadSeq = 0;

  constructor(
    private entity: InvestmentCompanyEntityService,
    private inCompaniesService: InvestmentInCompaniesService,
    private dialog: MatDialog,
    private confirm: ConfirmService,
    private notify: NotificationService,
    private tier: TierService,
  ) { }

  get paymentLabel(): string {
    return investmentPaymentLabel(this.row);
  }

  get paymentVariant() {
    return investmentPaymentVariant(this.row);
  }

  get accountLabel(): string {
    if (!this.row) return '—';
    return [this.row.accountingModelsNumber, this.row.accountingModelsName].filter(Boolean).join(' · ') || '—';
  }

  get hasFile(): boolean {
    return !!this.row && (this.row.isImage === true || this.row.isPdf === true);
  }

  get isPaid(): boolean {
    return !!this.row && this.row.investmentValue > 0 && this.row.pendingAmount <= 0;
  }

  hasValue(value: any): boolean {
    return value !== null && value !== undefined && value !== '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['row'] || !this.row || !this.row.uid) return;
    this.load(this.row.uid);
  }

  retry(): void {
    if (!this.row || !this.row.uid) return;
    this.load(this.row.uid);
  }

  add(): void {
    this.openPayments();
  }

  manage(): void {
    this.openPayments();
  }

  editLine(line: InvestmentDepositLine): void {
    this.openPayments(line.uid);
  }

  deleteLine(line: InvestmentDepositLine): void {
    if (!this.tier.requireAdvancedTier()) return;
    this.confirm.open({
      title: 'Eliminar pago',
      subtitle: this.row.investmentDescription || undefined,
      message: 'Se eliminará el pago de la inversión y su asiento contable. Si estaba conciliado, el movimiento bancario volverá a quedar pendiente. Esta acción no se puede deshacer.',
      tone: 'danger',
      details: this.details(line),
      confirmText: 'Eliminar',
      confirmIcon: 'delete',
      loadingText: 'Eliminando pago…',
      errorText: 'No se pudo eliminar el pago',
      action: () => this.inCompaniesService.depositDelete(line.uid).then(ok => { if (ok !== true) throw new Error(); }),
    }).subscribe(deleted => {
      if (!deleted) return;
      this.notify.ok('Pago eliminado correctamente');
      this.changed.emit();
    });
  }

  trackByUid(_: number, line: InvestmentDepositLine): string {
    return line.uid;
  }

  private openPayments(editUid?: string): void {
    if (!this.tier.requireAdvancedTier()) return;
    InvestmentInCompaniesDialogPayComponent.open(this.dialog, { investment: this.row, editUid })
      .subscribe(changed => { if (changed) this.changed.emit(); });
  }

  private load(uid: string): void {
    const seq = ++this.loadSeq;
    this.loading = !this.lines.length && !this.depositsCount;
    this.error = false;

    this.entity.deposits(uid)
      .then(deposits => {
        if (seq !== this.loadSeq || !this.row || uid !== this.row.uid) return;
        const list = Array.isArray(deposits) ? deposits : [];
        const ofYear = list
          .filter(item => item.depositDate && new Date(item.depositDate).getFullYear() === this.year)
          .sort((a, b) => new Date(b.depositDate).getTime() - new Date(a.depositDate).getTime());

        this.depositsCount = list.length;
        this.commissionsTotal = roundMoney(list.reduce((acc, item) => acc + (Number(item.commissions) || 0), 0));
        this.yearCount = ofYear.length;
        this.yearTotal = roundMoney(ofYear.reduce((acc, item) => acc + (Number(item.amount) || 0), 0));
        this.lines = ofYear.slice(0, MAX_LINES).map(item => this.toLine(item));
      })
      .catch(() => { if (seq === this.loadSeq) this.error = true; })
      .then(() => { if (seq === this.loadSeq) this.loading = false; });
  }

  private toLine(item: InvestmentInCompaniesDepositsModel): InvestmentDepositLine {
    return {
      uid: item.uid,
      date: item.depositDate,
      concept: item.typePay || 'Sin forma de pago',
      meta: [
        item.bankAccountInfo || '',
        item.commissions ? 'Comisión ' + Utils.FormatMoney(item.commissions) + ' €' : '',
        item.conciliationId ? 'Conciliado' : '',
      ].filter(Boolean).join(' · '),
      amount: Number(item.amount) || 0,
      source: item,
    };
  }

  private details(line: InvestmentDepositLine): ConfirmDialogDetail[] {
    const details: ConfirmDialogDetail[] = [];
    if (line.date) details.push({ label: 'Fecha', value: formatDate(line.date, 'dd/MM/yyyy', 'es-ES') });
    details.push({ label: 'Forma de pago', value: line.concept + (line.source.bankAccountInfo ? ' · ' + line.source.bankAccountInfo : '') });
    details.push({ label: 'Importe', value: Utils.FormatMoney(line.amount) + ' €' });
    return details;
  }
}
